import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** 復元できる契約 1 行（サーバの CenterRestorationRow と同じ欄。文言はサーバが作る）。 */
export interface RestorationRow {
    readonly tenantId: number;
    readonly company: string;
    readonly title: string;
    readonly planLabel: string;
    /** 解約した日時と、復元できるいちばん後の日時（日本時間の yyyy/MM/dd HH:mm）。 */
    readonly cancelledAt: string;
    readonly restorableUntil: string;
    /** 復元した後のお支払方法（カード払いだった契約は「カード払い → 請求書払い」）。 */
    readonly payment: string;
    /** カード払いを請求書払いに変えて復元するか（確認ダイアログの文を変える）。 */
    readonly becomesInvoice: boolean;
    /** 今復元したときに請求が再び始まる月（「2026年11月分から」。無償は「-」）。 */
    readonly billingResumes: string;
}

/** 契約の復元の画面（<c>GET /api/admin-center/restorations</c>。サーバの CenterRestorationViewModel と同じ欄）。 */
export interface RestorationList {
    /** 解約から 30 日以内の契約（解約した日時の新しい順。API が並べる）。 */
    readonly rows: readonly RestorationRow[];
    /** 復元できる日数（30）。 */
    readonly restorableDays: number;
}

/**
 * 最後の確認（重要な操作は最後にダイアログで確かめる。docs/01 §7）。
 * <b>カード払いだった契約は、請求書払いになることを書く</b>（PAY.JP の定期課金は戻せない）。
 */
export const restoreDialog = (row: RestorationRow): string =>
    `「${row.title}」（契約 ID ${String(row.tenantId)}）を復元します。プリザンターはすぐに使えるようになり、契約者にお知らせのメールを送ります。`
    + (row.becomesInvoice ? "カード払いだったため、請求書払いで復元します。" : "")
    + (row.billingResumes === "-" ? "" : `請求は ${row.billingResumes}再び始まります。`)
    + "よろしいですか？";

/** 1 行の「復元する」。終われば復元した契約 1 件の画面へ移る（API が返す）。 */
function RestoreForm({ row }: { readonly row: RestorationRow }): ReactElement {
    const form = useApiForm(adminCenterApi.restore(row.tenantId), undefined, restoreDialog(row));

    return (
        <form method="post" onSubmit={form.onSubmit} data-center-restore-form={String(row.tenantId)}>
            <FormSummary messages={form.summary} />
            <button type="submit" className="btn-secondary btn-sm" disabled={form.busy}>復元する</button>
        </form>
    );
}

/**
 * 管理センターからの契約の復元（解約の取り消し。Implem.Portal.Backend の docs/features/38）。
 * <b>解約から 30 日以内の契約だけを並べる</b>（過ぎた契約は API が返さない）。請求の権限を持つ運営だけが開ける。
 */
export function RestorePage(): ReactElement | null {
    const data = useApiData<RestorationList>(adminCenterApi.restorations);
    usePageMeta(titleFor(data, "契約の復元"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="復元できる契約" />;
    }
    const { rows, restorableDays } = data.value;

    return (
        <div className="page max-w-6xl">
            <p className="mb-4"><Link to={adminCenterUrls.menu} className="btn-secondary btn-sm">管理センターへ戻る</Link></p>
            <div className="panel">
                <h1 className="page-title">契約の復元</h1>
                <p className="page-lead">
                    {`解約から ${String(restorableDays)} 日以内の契約を、解約した日時の新しい順に並べます。選んだ契約を解約の前の状態に戻します。`}
                </p>

                <div className="alert-info mb-4">
                    <strong>カード払いだった契約は請求書払いで戻ります</strong>（解約で PAY.JP の定期課金とカードの情報を消したため）。
                    カード払いに戻すには、契約者がプラン変更でお支払方法を変えます
                    （無償利用・月額以外の契約は契約者がプラン変更できないため、運営にお問い合わせいただきます）。
                    解約した月までの請求は解約の前のお支払方法のままで、請求の再開の月から請求書払いで請求します。
                </div>

                {rows.length === 0
                    ? <div className="empty-state text-sm text-slate-500">{`解約から ${String(restorableDays)} 日以内の契約はありません。`}</div>
                    : (
                        <div className="table-card">
                            <div className="table-scroll">
                                <table className="data-table" data-center-restorations="">
                                    <thead>
                                        <tr>
                                            <th scope="col">契約 ID</th>
                                            <th scope="col">企業名</th>
                                            <th scope="col">システム名</th>
                                            <th scope="col">プラン</th>
                                            <th scope="col">解約した日時</th>
                                            <th scope="col">復元できる期限</th>
                                            <th scope="col">お支払方法</th>
                                            <th scope="col">請求の再開</th>
                                            <th scope="col"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr key={row.tenantId}>
                                                <td>{row.tenantId}</td>
                                                <td className="font-medium text-ink-800">{row.company}</td>
                                                <td>{row.title}</td>
                                                <td>{row.planLabel}</td>
                                                <td>{row.cancelledAt}</td>
                                                <td>{row.restorableUntil}</td>
                                                <td>{row.payment}</td>
                                                <td>{row.billingResumes}</td>
                                                <td className="cell-actions"><RestoreForm row={row} /></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
}
